import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Panel = ({ title, note, rows, cols, testid }) => (
  <div className="border border-zinc-200 rounded-sm mb-6" data-testid={testid}>
    <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600">{title} ({rows.length})</p>
      <p className="text-[10px] text-zinc-400">{note}</p>
    </div>
    {rows.length === 0 ? (
      <p className="px-3 py-5 text-xs text-zinc-400 text-center">Nothing to show</p>
    ) : (
      <table className="data-table w-full">
        <thead><tr>{cols.map(c => <th key={c.key} className={c.num ? "num" : ""}>{c.label}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} data-testid={`${testid}-row-${i}`}>
            {cols.map(c => (
              <td key={c.key} className={`text-[10px] ${c.num ? "num font-mono" : ""}`}>
                {c.money ? fmt(r[c.key]) : (r[c.key] === "" || r[c.key] == null ? "—" : String(r[c.key]))}
              </td>
            ))}
          </tr>
        ))}</tbody>
      </table>
    )}
  </div>
);

export default function BlinkitChecksPage() {
  const [w, setW] = useState(null);
  const [un, setUn] = useState({ invoices: [], deductions: [] });
  const [linking, setLinking] = useState(null);
  const [orderNumber, setOrderNumber] = useState("");
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/pulse/blinkit/warnings"),
        api.get("/pulse/blinkit/unmatched"),
      ]);
      setW(a.data);
      setUn(b.data);
      setLoadError("");
    } catch (e) {
      setLoadError(e.response?.status === 403
        ? "You do not have access to the Blinkit checks. Ask an administrator for the Pulse view or the record-payment permission."
        : "Could not load the Blinkit checks. Please try again.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const link = async (id) => {
    if (!orderNumber.trim()) return toast.error("Type the ORBIT order number");
    try {
      await api.post(`/pulse/blinkit/unmatched/${id}/link-order`, { order_number: orderNumber.trim() });
      toast.success("Linked — the deduction is posted on the next upload");
      setLinking(null); setOrderNumber(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not link"); }
  };

  return (
    <div className="p-6" data-testid="blinkit-checks-page">
      <div className="mb-5">
        <h1 className="font-heading text-2xl font-semibold text-zinc-950">Blinkit Checks</h1>
        <p className="text-xs text-zinc-500 mt-1">
          What the Blinkit invoice, debit-note and purchase-return files say against our own orders.
          {w?.as_of ? ` Built from the files uploaded up to ${w.as_of}.` : " Upload the Blinkit files to build these lists."}
        </p>
      </div>

      {loadError ? (
        <div className="border border-amber-300 bg-amber-50 rounded-sm p-6 text-center" data-testid="blinkit-checks-error">
          <p className="text-xs text-amber-800">{loadError}</p>
        </div>
      ) : (
      <>
      <Panel testid="paid-but-short" title="Blinkit marked paid, we are short"
             note="Blinkit's invoice is complete and paid, but ORBIT has received less than the payable amount."
             rows={w?.paid_but_short || []}
             cols={[{ key: "order", label: "Order" }, { key: "invoice", label: "Invoice" },
                    { key: "payable", label: "Payable", num: true, money: true },
                    { key: "received", label: "Received", num: true, money: true },
                    { key: "short_by", label: "Short by", num: true, money: true }]} />

      <Panel testid="short-grn" title="GRN short against what we dispatched"
             note="Blinkit received fewer units than we sent — expect a debit note for the difference."
             rows={w?.short_grn || []}
             cols={[{ key: "order", label: "Order" }, { key: "invoice", label: "Invoice" },
                    { key: "dispatched", label: "Dispatched", num: true },
                    { key: "grn_qty", label: "GRN", num: true },
                    { key: "short_units", label: "Short units", num: true },
                    { key: "shortfall", label: "Expected shortfall", num: true, money: true }]} />

      <Panel testid="excess-received" title="Excess received by Blinkit (not paid)"
             note="For the warehouse — Blinkit's debit note says they took more than the PO."
             rows={w?.excess_received || []}
             cols={[{ key: "order", label: "Order" }, { key: "dn_id", label: "DN" },
                    { key: "sku", label: "SKU" }, { key: "qty", label: "Qty", num: true },
                    { key: "facility", label: "Facility" }]} />

      <Panel testid="not-posted-closed" title="Not posted — order closed or cancelled"
             note="These file lines were left alone because the order is closed, short paid, cancelled, rejected or deleted. To post one, raise the closed-order change request on the order."
             rows={w?.not_posted_closed || []}
             cols={[{ key: "order", label: "Order" }, { key: "status", label: "Status" },
                    { key: "invoice", label: "Invoice" }, { key: "type", label: "Type" },
                    { key: "amount", label: "Amount", num: true, money: true }]} />

      <Panel testid="need-to-pick" title="Blinkit wants us to collect"
             note="These lines also appear on the Stock on Hold report."
             rows={w?.need_to_pick || []}
             cols={[{ key: "order", label: "Order" }, { key: "dn_id", label: "DN" },
                    { key: "sku", label: "SKU" }, { key: "qty", label: "Qty", num: true },
                    { key: "facility", label: "Facility" }]} />

      <div className="border border-zinc-200 rounded-sm" data-testid="unmatched-panel">
        <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600">
            Rows with no ORBIT order ({(un.invoices || []).length + (un.deductions || []).length})
          </p>
          <p className="text-[10px] text-zinc-400">Link one to an order and its deduction is posted on the next upload.</p>
        </div>
        {((un.invoices || []).length + (un.deductions || []).length) === 0 ? (
          <p className="px-3 py-5 text-xs text-zinc-400 text-center">Every row found its order</p>
        ) : (
          <table className="data-table w-full">
            <thead><tr><th>Kind</th><th>Reference</th><th>Invoice</th><th>Facility</th><th>SKU</th><th className="num">Amount</th><th></th></tr></thead>
            <tbody>
              {[...(un.invoices || []), ...(un.deductions || [])].map((r, i) => (
                <tr key={r.id} data-testid={`unmatched-row-${i}`}>
                  <td className="text-[10px] uppercase">{r.kind}</td>
                  <td className="text-[10px] font-mono">{r.reference || r.invoice_number || "—"}</td>
                  <td className="text-[10px] font-mono">{r.invoice_number || "—"}</td>
                  <td className="text-[10px]">{r.facility || "—"}</td>
                  <td className="text-[10px]">{r.sku || "—"}</td>
                  <td className="num font-mono text-[10px]">{fmt(r.amount)}</td>
                  <td>
                    {linking === r.id ? (
                      <div className="flex gap-1 items-center">
                        <Input value={orderNumber} onChange={e => setOrderNumber(e.target.value)}
                               placeholder="ORD-00000" className="h-7 w-32 text-[10px] rounded-sm"
                               data-testid="link-order-input" />
                        <Button className="h-7 text-[10px] rounded-sm" onClick={() => link(r.id)} data-testid="link-order-save">Link</Button>
                        <Button variant="outline" className="h-7 text-[10px] rounded-sm" onClick={() => setLinking(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <button className="text-[10px] text-zinc-500 underline hover:text-zinc-900"
                              onClick={() => { setLinking(r.id); setOrderNumber(""); }}
                              data-testid={`link-order-btn-${i}`}>Link to order</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}
    </div>
  );
}
